'use client';

import React from 'react';
import Link, { usePathname, useRouter } from '@/core/i18n/navigation';
import Image from 'next/image';
import { ShoppingCart, User, LogIn, ChevronDown, Menu, X, Search, Grid, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { buildCategoryTree, CategoryFlat, CategoryNode } from '@/core/utils/category-tree';
import { splitPhones, telHref } from '@/core/utils/phone';
import { cn } from '@/core/utils/cn';
import { useTranslation } from '@/core/hooks/useTranslation';
import { toOptimizableSrc, getAbsoluteMediaUrl } from '@/core/utils/media';
import { optimizedImageUrl } from '@/core/utils/optimizedImageUrl';
import { fetchAPI } from '@/core/api/client';
import { trackClick } from '@/core/utils/analytics';
import { useAutoTranslateLabels } from '@/core/hooks/useAutoTranslateLabels';
import { useHeaderTransparency } from '@/core/components/providers/HeaderTransparencyContext';
import { DynamicQRCode } from '@/core/components/ui/QRCode';
import dynamic from 'next/dynamic';
// Radix Select (+ floating-ui, ~35 KB minified) is only used by the mobile drawer's dropdown-style
// language/currency pickers; imported statically it shipped with every page of every shop.
const LanguageDropdown = dynamic(() => import('./MobileMenuSelects').then((m) => m.LanguageDropdown));
const CurrencyDropdown = dynamic(() => import('./MobileMenuSelects').then((m) => m.CurrencyDropdown));
import { LottiePlayer } from '@/core/components/ui/lottie-player';
import { isShopStorefrontPath, isSiteMode } from '@/core/entitlements';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { priceOnRequestLabel } from '@/core/utils/product-action';
import { DesktopNav } from './DesktopNav';

interface MenuItemConfig {
    label: string;
    url: string;
    is_category_tree: boolean;
    translation_key?: string;
    children?: MenuItemConfig[];
    image_id?: number | null;
    image_url?: string | null;
    image_side?: 'left' | 'right';
    image_anchor?: 'top' | 'center' | 'bottom';
    image_size_percent?: number;
    image_fade_enabled?: boolean;
    image_y_offset?: number;
    is_bold?: boolean;
    font_weight?: string;
    text_color?: string;

    modal_enabled?: boolean;
    modal_title?: string;
    modal_subtitle?: string;
    modal_qr_url?: string;
    modal_image_url?: string;
    modal_body?: string;
    modal_desktop_only?: boolean;
    /** Defaults to shown (true when unset) — an explicit opt-out so existing
     *  configured modals keep behaving exactly as before this field existed. */
    modal_show_browser_button?: boolean;
}

/** Renders the cart/account glyph — a merchant-uploaded custom icon if set, otherwise the default lucide icon. */
function HeaderIconGlyph({
    iconKey,
    cfg,
    loggedIn,
    style,
}: {
    iconKey: 'cart' | 'account' | 'hamburger';
    cfg: HeaderConfig;
    loggedIn?: boolean;
    style: React.CSSProperties;
}) {
    const customUrl = iconKey === 'cart' ? cfg.cart_icon_url : iconKey === 'account' ? cfg.account_icon_url : cfg.hamburger_icon_url;
    if (customUrl) {
        // width/height are just for the optimizer's own srcset generation — actual on-page
        // size still comes from the --header-icon-size CSS var via style, same split used for
        // the site logo above (real uploads run 400px+, way bigger than the ~20-38px this
        // ever renders at).
        const iconSize = cfg.icon_size_desktop ?? cfg.icon_size_mobile ?? 20;
        return (
            <Image
                src={toOptimizableSrc(getAbsoluteMediaUrl(customUrl))}
                alt=""
                width={iconSize}
                height={iconSize}
                style={{ width: style.width, height: style.height, objectFit: 'contain' }}
            />
        );
    }
    if (iconKey === 'cart') return <ShoppingCart style={style} />;
    if (iconKey === 'hamburger') return <Menu style={style} />;
    return loggedIn ? <User style={style} /> : <LogIn style={style} />;
}

// Map of common Polish nav labels to translation keys
const LABEL_TO_KEY: Record<string, string> = {
    'Główna': 'nav.home',
    'Sklep': 'nav.store',
    'Produkty': 'nav.products',
    'Koszyk': 'nav.cart',
    'Moje konto': 'nav.account',
    'Zaloguj się': 'nav.login',
    'Wyloguj': 'nav.logout',
    'Kontakt': 'footer.contact',
    'Menu': 'nav.menu',
};

export interface HeaderConfig {
    sticky?: boolean;
    height?: number;
    height_mobile?: number;
    bg_color?: string;
    text_color?: string;
    // Topbar (Pasek górny ogłoszeń)
    topbar_enabled?: boolean;
    topbar_bg_color?: string;
    topbar_text_color?: string;
    topbar_content?: string;
    topbar_link?: string;
    topbar_phone?: string;
    topbar_height?: number;
    topbar_font_size?: number;
    topbar_font_weight?: string | number;
    topbar_alignment?: 'center' | 'between' | 'left' | 'right';
    topbar_badge_text?: string;
    topbar_badge_bg?: string;
    topbar_badge_text_color?: string;
    topbar_email?: string;
    topbar_show_socials?: boolean;
    topbar_social_links?: { platform: string; url: string }[];
    topbar_featured_brand_ids?: number[];
    border_style?: 'none' | 'bottom' | 'all';
    border_color?: string;
    border_width?: number;
    border_type?: 'solid' | 'dashed' | 'dotted';
    border_shadow?: boolean;
    border_shadow_color?: string;
    border_shadow_size?: number;
    logo_id?: number | null;
    // Logo sizing
    logo_max_height?: number;
    logo_max_width?: number;
    logo_max_height_mobile?: number;
    logo_padding_x?: number;
    logo_padding_y?: number;
    // Icon sizes
    icon_size_desktop?: number;
    icon_size_mobile?: number;
    // Icon order & custom icons (cart / account-login / language / currency)
    icon_order?: ('cart' | 'account' | 'language' | 'currency')[];
    // Logo / Nav / Icons column widths (%, should sum to 100) for the desktop 3-column header grid
    column_widths?: { logo: number; menu: number; icons: number };
    cart_icon_id?: number | null;
    cart_icon_url?: string | null;
    account_icon_id?: number | null;
    account_icon_url?: string | null;
    hamburger_icon_id?: number | null;
    hamburger_icon_url?: string | null;
    // Mobile menu styling & position
    mobile_menu_position?: 'left' | 'right' | 'top' | 'fullscreen';
    mobile_menu_font_family?: string;
    mobile_menu_font_size?: number;
    mobile_menu_font_weight?: string;
    mobile_menu_alignment?: 'left' | 'center' | 'right';
    mobile_menu_item_spacing?: number;
    mobile_menu_bg_color?: string;
    mobile_menu_text_color?: string;
    // Menu link styles
    menu_hover_color?: string;
    menu_hover_bg?: string;
    menu_active_color?: string;
    menu_active_bg?: string;
    menu_item_padding_x?: number;
    menu_item_padding_y?: number;
    menu_item_border_radius?: number;
    menu_valign?: 'center' | 'bottom' | 'top';
    menu_y_offset?: number;
    nav_layout?: 'single_row' | 'two_rows';

    // Dropdown styles
    dropdown_bg?: string;
    dropdown_text_color?: string;
    dropdown_hover_bg?: string;
    dropdown_border_radius?: number;
    dropdown_shadow?: boolean;
    // Menu content
    menu_items?: MenuItemConfig[];
    show_language_selector?: boolean;
    show_currency_selector?: boolean;
    /** A shop that sells something without a basket (e.g. room bookings) can drop these icons. */
    hide_cart_icon?: boolean;
    hide_account_icon?: boolean;
    // Layout of the language/currency pickers inside the mobile hamburger drawer.
    // 'auto' switches from a button grid to a dropdown once there are more than
    // 4 active options, so the drawer doesn't get cramped with many languages.
    mobile_menu_selector_style?: 'auto' | 'grid' | 'dropdown';
    // Which zone (left/center/right) each of the mobile top bar's logo, icon cluster, and
    // hamburger button lands in — defaults to logo:left, icons:right, hamburger:right (the
    // original fixed layout) when unset.
    mobile_header_layout?: { logo?: 'left' | 'center' | 'right'; icons?: 'left' | 'center' | 'right'; hamburger?: 'left' | 'center' | 'right' };
    // Typography
    font_family?: string;
    menu_font_weight?: string;
    menu_font_size?: number;
    menu_letter_spacing?: string;

    // Additional subbar (Dodatkowy pasek)
    subbar_enabled?: boolean;
    subbar_bg_color?: string;
    subbar_text_color?: string;
    subbar_border_style?: string;
    subbar_border_color?: string;
    subbar_border_width?: number;
    subbar_border_type?: 'solid' | 'dashed' | 'dotted';
    subbar_border_shadow?: boolean;
    subbar_border_shadow_color?: string;
    subbar_border_shadow_size?: number;
    subbar_height?: number;
    subbar_height_mobile?: number;
    subbar_expanded_homepage?: boolean;
    subbar_show_search?: boolean;

    // Dropdown / Mega Menu w dodatkowym pasku
    subbar_dropdown_bg?: string;
    subbar_dropdown_sidebar_bg?: string;
    subbar_dropdown_text_color?: string;
    subbar_dropdown_active_text_color?: string;
    subbar_dropdown_active_bg?: string;
    subbar_dropdown_hover_bg?: string;
    subbar_dropdown_hover_text?: string;

    // Przycisk "Wszystkie kategorie"
    subbar_button_bg?: string;
    subbar_button_text_color?: string;
    subbar_button_hover_bg?: string;
    subbar_button_hover_text?: string;
    subbar_button_border_radius?: number;

    // Pole wyszukiwarki (Input & Przycisk)
    subbar_search_input_bg?: string;
    subbar_search_input_border_color?: string;
    subbar_search_input_text_color?: string;
    subbar_search_input_placeholder_color?: string;
    subbar_search_input_border_radius?: number;
    subbar_search_button_bg?: string;
    subbar_search_button_text_color?: string;
    subbar_search_button_hover_bg?: string;
    subbar_search_button_hover_text?: string;
    subbar_search_button_border_radius?: number;
}

interface HeaderProps {
    themeColor: string;
    siteName: string;
    headerConfig?: HeaderConfig | null;
    logoUrl?: string | null;
    /** The logo file's intrinsic pixel size, measured server-side (store layout) — lets the
     *  <img> reserve its real width before it loads. Null/absent: previous behaviour. */
    logoSize?: { width: number; height: number } | null;
    categories?: CategoryFlat[];
    brands?: { id: number; name: string; logo_url?: string | null }[];
}

// Matches the icon set used by the footer's own social block (modules/shop/components/layout/footer.tsx) —
// kept as a separate copy here since the two bars have independent color/size needs.
const TOPBAR_SOCIAL_ICONS: Record<string, string> = {
    facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
    instagram: 'M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37zM17.5 6.5h.01M7.5 3.5h9a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9a4 4 0 0 1-4-4v-9a4 4 0 0 1 4-4z',
    youtube: 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z',
    tiktok: 'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.24 8.24 0 0 0 4.83 1.55V6.79a4.85 4.85 0 0 1-1.06-.1z',
    linkedin: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    x: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    google: 'M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12s3.36-7.27 7.19-7.27c3.08 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.19 2C6.42 2 1.9 6.8 1.9 12s4.52 10 10.29 10c5.35 0 9.25-3.67 9.25-9.09 0-1.15-.17-1.81-.17-1.81z',
};

function TopbarSocialIcon({ platform }: { platform: string }) {
    const d = TOPBAR_SOCIAL_ICONS[platform.toLowerCase()];
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
            {d ? <path d={d} /> : <>
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </>}
        </svg>
    );
}

// ── Category dropdown (desktop) ──────────────────────────────────────

// ── Submenu Item (Recursive) ──────────────────────────────────────────

function SubmenuItem({
    item,
    textColor,
    hoverBg,
    isFirstLevel = true
}: {
    item: MenuItemConfig;
    textColor: string;
    hoverBg: string;
    isFirstLevel?: boolean;
}) {
    const [isHovered, setIsHovered] = useState(false);
    const hasChildren = item.children && item.children.length > 0;

    return (
        <li className="list-none">
            <Link
                href={item.url}
                className={cn(
                    "group flex items-center justify-between px-3.5 py-2.5 text-[13px] font-medium transition-all duration-200 rounded-lg mx-2",
                    isFirstLevel ? "mt-1" : "mt-0.5"
                )}
                style={{
                    color: textColor,
                    backgroundColor: isHovered ? hoverBg : 'transparent',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <span className="relative">
                    {item.label}
                    {isHovered && <span className="absolute -bottom-1 left-0 w-full h-[1px] bg-current opacity-20" />}
                </span>
                {hasChildren && (
                    <ChevronDown className={cn("w-3 h-3 opacity-40 transition-transform duration-300", isHovered ? "-rotate-90" : "")} />
                )}
            </Link>

            {hasChildren && (
                <ul className={cn(
                    "ml-6 mt-1 mb-2 border-l-2 pl-1 space-y-1 transition-all",
                    isHovered ? "border-current/20" : "border-current/5"
                )} style={{ borderColor: `${textColor}15` }}>
                    {item.children?.map((child, idx) => (
                        <SubmenuItem 
                            key={idx} 
                            item={child} 
                            textColor={textColor} 
                            hoverBg={hoverBg} 
                            isFirstLevel={false} 
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}


function CategoryMenuDropdown({
    label,
    categories,
    cfg,
}: {
    label: string;
    categories: CategoryFlat[];
    cfg: HeaderConfig;
}) {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tree = buildCategoryTree(categories);

    const handleMouseEnter = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const handleMouseLeave = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 150);
    };

    const textColor = cfg.text_color ?? '#ffffff';
    const hoverColor = cfg.menu_hover_color || textColor;
    const hoverBg = cfg.menu_hover_bg || 'transparent';
    const px = cfg.menu_item_padding_x ?? 8;
    const py = cfg.menu_item_padding_y ?? 4;
    const br = cfg.menu_item_border_radius ?? 6;
    const dropBg = cfg.dropdown_bg || 'rgba(17,24,39,0.95)';
    const dropText = cfg.dropdown_text_color || '#ffffff';
    const dropHoverBg = cfg.dropdown_hover_bg || 'rgba(255,255,255,0.08)';
    const dropBr = cfg.dropdown_border_radius ?? 8;
    const dropShadow = cfg.dropdown_shadow !== false;

    return (
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                className="flex items-center gap-1.5 transition-all text-sm font-semibold tracking-tight whitespace-nowrap"
                style={{
                    color: hovered ? hoverColor : textColor,
                    backgroundColor: hovered ? hoverBg : 'transparent',
                    padding: `${py}px ${px}px`,
                    borderRadius: `${br}px`,
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {label} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")} />
            </button>
            {open && tree.length > 0 && (
                <div
                    className="absolute left-0 top-full mt-1 min-w-52 max-h-80 overflow-y-auto z-[100] backdrop-blur-sm"
                    style={{
                        backgroundColor: dropBg,
                        borderRadius: `${dropBr}px`,
                        boxShadow: dropShadow ? '0 10px 40px rgba(0,0,0,0.25)' : undefined,
                    }}
                >
                    <ul className="py-2">
                        {tree.map(node => {
                            const mapNode = (n: CategoryNode, parentPath: string = ''): MenuItemConfig => {
                                const currentPath = parentPath ? `${parentPath}/${n.slug}` : n.slug;
                                return {
                                    label: n.name,
                                    url: `/kategoria/${currentPath}`,
                                    is_category_tree: false,
                                    children: n.children.map(c => mapNode(c, currentPath))
                                };
                            };
                            return (
                                <SubmenuItem
                                    key={node.id}
                                    item={mapNode(node)}
                                    textColor={dropText}
                                    hoverBg={dropHoverBg}
                                />
                            );
                        })}
                    </ul>

                </div>
            )}
        </div>
    );
}

function HeaderLinkModal({
    item,
    onClose,
}: {
    item: MenuItemConfig;
    onClose: () => void;
}) {
    const title = item.modal_title || 'SKANUJ & GRAJ!';
    const subtitle = item.modal_subtitle || 'Zeskanuj smartphonem';
    const body = item.modal_body || 'Aplikacje działają tylko na urządzeniach mobilnych';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
            <div className="relative w-full max-w-md bg-[#FFCF00] p-4 sm:p-6 rounded-[36px] shadow-2xl transition-all scale-100" onClick={e => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/10 hover:bg-black/20 text-slate-800 flex items-center justify-center transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
                <div className="bg-white rounded-[28px] p-6 text-center space-y-5 shadow-inner">
                    <h2 className="font-extrabold text-2xl sm:text-3xl tracking-tight text-slate-900 uppercase">
                        {title}
                    </h2>
                    
                    <div className="mx-auto w-48 h-48 bg-slate-50 border-2 border-dashed border-amber-300 rounded-2xl flex flex-col items-center justify-center p-3 relative overflow-hidden">
                        {item.modal_qr_url ? (
                            <img src={getAbsoluteMediaUrl(item.modal_qr_url)} alt="QR Code" className="w-full h-full object-contain" />
                        ) : item.url && item.url !== '#' ? (
                            <DynamicQRCode value={typeof window !== 'undefined' ? `${window.location.origin}${item.url}` : item.url} size={160} />
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <div className="p-3 bg-amber-100 rounded-xl text-amber-800">
                                    <Grid className="w-10 h-10" />
                                </div>
                                <span className="text-[11px] font-bold text-slate-500">Kod QR aplikacji</span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-1 pt-1">
                        <p className="font-extrabold text-slate-900 text-base">
                            {subtitle}
                        </p>
                        <p className="text-xs font-medium text-slate-500 max-w-xs mx-auto">
                            {body}
                        </p>
                    </div>

                    {item.modal_show_browser_button !== false && item.url && item.url !== '#' && (
                        <div className="pt-2">
                            <Link
                                href={item.url}
                                onClick={onClose}
                                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-colors shadow-md"
                            >
                                Otwórz w przeglądarce
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function NavLink({
    item,
    cfg,
    isSolid = true,
    onOpenModal,
    alignDropdown = 'left',
}: {
    item: MenuItemConfig;
    cfg: HeaderConfig;
    isSolid?: boolean;
    onOpenModal?: (item: MenuItemConfig) => void;
    /** "right" for the trailing "Więcej" entry, so its dropdown opens inwards. */
    alignDropdown?: 'left' | 'right';
}) {
    const pathname = usePathname();
    const [hovered, setHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isActive = pathname === item.url || (item.url !== '/' && pathname?.startsWith(item.url));
    
    const textColor = cfg.text_color ?? '#ffffff';
    const hoverColor = cfg.menu_hover_color || textColor;
    const hoverBg = cfg.menu_hover_bg || 'transparent';
    const px = cfg.menu_item_padding_x ?? 12;
    const py = cfg.menu_item_padding_y ?? 6;
    const br = cfg.menu_item_border_radius ?? 8;
    
    // Active styles
    const activeColor = cfg.menu_active_color || hoverColor;
    const activeBg = cfg.menu_active_bg || 'transparent';

    const hasChildren = item.children && item.children.length > 0;

    const handleMouseEnter = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setHovered(true);
        if (hasChildren) setIsMenuOpen(true);
    };

    const handleMouseLeave = () => {
        setHovered(false);
        if (hasChildren) {
            closeTimer.current = setTimeout(() => setIsMenuOpen(false), 150);
        }
    };

    const isBold = item.is_bold || item.font_weight === 'bold' || item.font_weight === '700';

    const baseStyle: React.CSSProperties = {
        color: hovered ? hoverColor : (isActive ? activeColor : (item.text_color || textColor)),
        backgroundColor: hovered ? hoverBg : (isActive ? activeBg : 'transparent'),
        padding: `${py}px ${px}px`,
        borderRadius: `${br}px`,
        fontFamily: cfg.font_family || undefined,
        fontWeight: isBold ? '700' : (item.font_weight || cfg.menu_font_weight || undefined),
        fontSize: cfg.menu_font_size ? `${cfg.menu_font_size}px` : undefined,
        letterSpacing: cfg.menu_letter_spacing || undefined,
        transform: cfg.menu_y_offset ? `translateY(${cfg.menu_y_offset}px)` : undefined,
    };

    const dropBg = cfg.dropdown_bg || 'rgba(255,255,255,1)';
    const dropText = cfg.dropdown_text_color || '#111827';
    const dropHoverBg = cfg.dropdown_hover_bg || 'rgba(0,0,0,0.05)';
    const dropBr = cfg.dropdown_border_radius ?? 12;
    const dropShadow = cfg.dropdown_shadow !== false;

    const imageSide = item.image_side || 'right';
    const imageAnchor = item.image_anchor || 'bottom';
    const imageSizePercent = item.image_size_percent ?? 60;
    const headerHeight = cfg.height ?? 64;
    const imageHeightPx = Math.round((headerHeight * imageSizePercent) / 100);
    const imageYOffset = item.image_y_offset || 0;

    const linkImage = item.image_url ? (
        <div 
            className="overflow-hidden h-full self-stretch flex shrink-0 pointer-events-none"
            style={{ maxHeight: `${headerHeight}px` }}
        >
            <img
                src={getAbsoluteMediaUrl(item.image_url)}
                alt=""
                aria-hidden="true"
                style={{
                    height: `${imageHeightPx}px`,
                    width: 'auto',
                    maxWidth: 'none',
                    transform: imageYOffset ? `translateY(${imageYOffset}px)` : undefined,
                }}
                className={cn(
                    "pointer-events-none transition-all duration-300",
                    imageAnchor === 'top' && "self-start",
                    imageAnchor === 'center' && "self-center",
                    imageAnchor === 'bottom' && "self-end",
                    item.image_fade_enabled ? (isSolid ? "opacity-100" : "opacity-0") : "opacity-100"
                )}
            />
        </div>
    ) : null;

    const handleLinkClick = (e: React.MouseEvent) => {
        if (item.modal_enabled) {
            e.preventDefault();
            onOpenModal?.(item);
        }
    };

    return (
        <div 
            className={cn(
                "relative h-full self-stretch flex gap-1.5",
                cfg.menu_valign === 'bottom' ? 'items-end' :
                cfg.menu_valign === 'top' ? 'items-start' :
                'items-center'
            )}
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={handleMouseLeave}
        >
            {imageSide === 'left' && linkImage}

            {hasChildren && item.url && item.url !== '#' ? (
                // A parent with its own page (e.g. a top category) stays clickable; hover still opens the dropdown.
                <Link
                    href={item.url}
                    className="flex items-center gap-1.5 transition-all text-sm font-semibold tracking-tight whitespace-nowrap"
                    style={baseStyle}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen}
                >
                    {item.label}
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", isMenuOpen && "rotate-180")} />
                </Link>
            ) : hasChildren ? (
                <button
                    className="flex items-center gap-1.5 transition-all text-sm font-semibold tracking-tight whitespace-nowrap"
                    style={baseStyle}
                >
                    {item.label}
                    <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", isMenuOpen && "rotate-180")} />
                </button>
            ) : item.modal_enabled ? (
                <button
                    type="button"
                    onClick={handleLinkClick}
                    className="text-sm font-semibold transition-all tracking-tight whitespace-nowrap text-left"
                    style={baseStyle}
                >
                    {item.label}
                </button>
            ) : (
                <Link
                    href={item.url}
                    className="text-sm font-semibold transition-all tracking-tight whitespace-nowrap"
                    style={baseStyle}
                >
                    {item.label}
                </Link>
            )}

            {imageSide === 'right' && linkImage}

            {isMenuOpen && hasChildren && (
                <div
                    className={cn("absolute top-full pt-2 z-[100] min-w-[200px]", alignDropdown === 'right' ? 'right-0' : 'left-0')}
                >
                    <div 
                        className="overflow-hidden border border-slate-100 shadow-2xl backdrop-blur-md"
                        style={{
                            backgroundColor: dropBg,
                            borderRadius: `${dropBr}px`,
                            boxShadow: dropShadow ? '0 20px 40px -10px rgba(0,0,0,0.15)' : undefined,
                        }}
                    >
                        <ul className="py-2">
                            {item.children?.map((child, idx) => (
                                <SubmenuItem 
                                    key={idx} 
                                    item={child} 
                                    textColor={dropText} 
                                    hoverBg={dropHoverBg} 
                                />
                            ))}
                        </ul>

                    </div>
                </div>
            )}
        </div>
    );
}

// ── Language & Currency Selector Constants ────────────────────────────

const LANGUAGE_TO_COUNTRY: Record<string, string> = {
    pl: 'pl',
    en: 'gb',
    de: 'de',
    es: 'es',
    fr: 'fr',
    cs: 'cz',
    sk: 'sk',
    it: 'it',
    nl: 'nl',
};

const getFlagUrl = (code: string) => {
    const country = LANGUAGE_TO_COUNTRY[code.toLowerCase()] || code.toLowerCase();
    return `https://flagcdn.com/w40/${country}.png`;
};

const ALL_LANGUAGES = [
    { code: 'pl', flag: '🇵🇱', label: 'PL', name: 'Polski' },
    { code: 'en', flag: '🇬🇧', label: 'EN', name: 'English' },
    { code: 'de', flag: '🇩🇪', label: 'DE', name: 'Deutsch' },
    { code: 'es', flag: '🇪🇸', label: 'ES', name: 'Español' },
    { code: 'fr', flag: '🇫🇷', label: 'FR', name: 'Français' },
    { code: 'cs', flag: '🇨🇿', label: 'CS', name: 'Čeština' },
    { code: 'sk', flag: '🇸🇰', label: 'SK', name: 'Slovenčina' },
    { code: 'it', flag: '🇮🇹', label: 'IT', name: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', label: 'NL', name: 'Nederlands' },
];

const MOBILE_SELECTOR_DROPDOWN_THRESHOLD = 4;

/** Decides grid-of-buttons vs. dropdown for the mobile drawer's language/currency
 * picker. 'auto' avoids a cramped multi-row button grid once there are enough
 * active options that a 288px-wide drawer can't lay them out in one clean row. */
const resolveSelectorLayout = (style: HeaderConfig['mobile_menu_selector_style'], optionCount: number): 'grid' | 'dropdown' => {
    if (style === 'grid' || style === 'dropdown') return style;
    return optionCount > MOBILE_SELECTOR_DROPDOWN_THRESHOLD ? 'dropdown' : 'grid';
};

const ALL_CURRENCIES = [
    { code: 'PLN', symbol: 'zł', name: 'Złoty (PLN)' },
    { code: 'EUR', symbol: '€', name: 'Euro (EUR)' },
    { code: 'USD', symbol: '$', name: 'Dolar (USD)' },
    { code: 'GBP', symbol: '£', name: 'Funt (GBP)' },
    { code: 'CZK', symbol: 'Kč', name: 'Korona (CZK)' }
];

function LanguageSelector({
    cfg,
    currentLocale,
    onChange,
    isMobile = false,
    supportedLocales = ['pl', 'en', 'de'],
}: {
    cfg: HeaderConfig;
    currentLocale: string;
    onChange: (lang: string) => void;
    isMobile?: boolean;
    supportedLocales?: string[];
}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const languages = ALL_LANGUAGES.filter(l => supportedLocales.includes(l.code));

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const activeLang = languages.find(l => l.code === currentLocale) || languages[0] || ALL_LANGUAGES[0];
    const textColor = cfg.text_color ?? '#ffffff';
    const px = cfg.menu_item_padding_x ?? 8;
    const py = cfg.menu_item_padding_y ?? 4;
    const br = cfg.menu_item_border_radius ?? 6;
    const dropBg = cfg.dropdown_bg || 'rgba(255,255,255,1)';
    const dropText = cfg.dropdown_text_color || '#111827';
    const dropHoverBg = cfg.dropdown_hover_bg || 'rgba(0,0,0,0.05)';
    const dropBr = cfg.dropdown_border_radius ?? 12;
    const dropShadow = cfg.dropdown_shadow !== false;

    return (
        <div className="relative shrink-0" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 transition-all text-xs font-bold uppercase tracking-wider select-none"
                style={{
                    color: textColor,
                    padding: `${py}px ${px}px`,
                    borderRadius: `${br}px`,
                }}
            >
                <img
                    src={getFlagUrl(activeLang.code)}
                    alt={activeLang.name}
                    className="object-cover rounded-sm shrink-0"
                    style={{ width: 'var(--header-flag-width)', height: 'var(--header-flag-height)' }}
                />
                {!isMobile && <span className="opacity-90">{activeLang.label}</span>}
                {!isMobile && <ChevronDown className={cn("h-3 w-3 opacity-60 transition-transform duration-200", open && "rotate-180")} />}
            </button>
            {open && (
                <div
                    className={cn(
                        "absolute mt-1 min-w-32 z-[100] overflow-hidden border border-slate-100/50 backdrop-blur-sm shadow-xl",
                        isMobile ? "right-0" : "left-0"
                    )}
                    style={{
                        backgroundColor: dropBg,
                        borderRadius: `${dropBr}px`,
                        boxShadow: dropShadow ? '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)' : undefined,
                    }}
                >
                    <ul className="py-1">
                        {languages.map(lang => (
                            <li key={lang.code}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(lang.code);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs font-semibold flex items-center gap-2.5 transition-colors"
                                    style={{
                                        color: dropText,
                                        backgroundColor: 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = dropHoverBg;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <img
                                        src={getFlagUrl(lang.code)}
                                        alt={lang.name}
                                        className="object-cover rounded-sm shrink-0"
                                        style={{ width: 'var(--header-flag-width)', height: 'var(--header-flag-height)' }}
                                    />
                                    <span>{lang.name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function CurrencySelector({
    cfg,
    currentCurrency,
    onChange,
    isMobile = false,
    supportedCurrencies = ['PLN', 'EUR', 'USD', 'GBP', 'CZK']
}: {
    cfg: HeaderConfig;
    currentCurrency: string;
    onChange: (curr: string) => void;
    isMobile?: boolean;
    supportedCurrencies?: string[];
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currencies = ALL_CURRENCIES.filter(c => supportedCurrencies.includes(c.code));
    const activeCurr = currencies.find(c => c.code === currentCurrency) || currencies[0] || ALL_CURRENCIES[0];
    const textColor = cfg.text_color ?? '#ffffff';
    const px = cfg.menu_item_padding_x ?? 8;
    const py = cfg.menu_item_padding_y ?? 4;
    const br = cfg.menu_item_border_radius ?? 6;
    const dropBg = cfg.dropdown_bg || 'rgba(255,255,255,1)';
    const dropText = cfg.dropdown_text_color || '#111827';
    const dropHoverBg = cfg.dropdown_hover_bg || 'rgba(0,0,0,0.05)';
    const dropBr = cfg.dropdown_border_radius ?? 12;
    const dropShadow = cfg.dropdown_shadow !== false;

    return (
        <div className="relative shrink-0" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1 transition-all font-black uppercase tracking-wider select-none"
                style={{
                    color: textColor,
                    padding: `${py}px ${px}px`,
                    borderRadius: `${br}px`,
                    fontSize: 'var(--header-currency-font-size)',
                }}
            >
                {isMobile ? (
                    <span className="opacity-90">{activeCurr.code}</span>
                ) : (
                    <>
                        <span className="opacity-80 mr-0.5">{activeCurr.symbol}</span>
                        <span className="opacity-90 mr-0.5">{activeCurr.code}</span>
                        <ChevronDown className={cn("h-3 w-3 opacity-60 transition-transform duration-200", open && "rotate-180")} />
                    </>
                )}
            </button>
            {open && (
                <div
                    className={cn(
                        "absolute mt-1 min-w-32 z-[100] overflow-hidden border border-slate-100/50 backdrop-blur-sm shadow-xl",
                        isMobile ? "right-0" : "left-0"
                    )}
                    style={{
                        backgroundColor: dropBg,
                        borderRadius: `${dropBr}px`,
                        boxShadow: dropShadow ? '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)' : undefined,
                    }}
                >
                    <ul className="py-1">
                        {currencies.map(curr => (
                            <li key={curr.code}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(curr.code);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs font-semibold flex items-center gap-2 transition-colors"
                                    style={{
                                        color: dropText,
                                        backgroundColor: 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = dropHoverBg;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <span className="w-5 text-center font-bold opacity-60">{curr.symbol}</span>
                                    <span>{t(`currency.${curr.code}`, curr.name)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ── Mobile drawer ─────────────────────────────────────────────────────

function MobileMenu({
    isOpen,
    onClose,
    menuItems,
    categories,
    session,
    cartCount,
    cfg,
    locale,
    onLocaleChange,
    currency,
    onCurrencyChange,
    supportedLocales = ['pl', 'en', 'de'],
    supportedCurrencies = ['PLN', 'EUR', 'USD', 'GBP', 'CZK'],
}: {
    isOpen: boolean;
    onClose: () => void;
    menuItems: MenuItemConfig[];
    categories: CategoryFlat[];
    session: any;
    cartCount: number;
    cfg: HeaderConfig;
    locale: string;
    onLocaleChange: (lang: string) => void;
    currency: string;
    onCurrencyChange: (curr: string) => void;
    supportedLocales?: string[];
    supportedCurrencies?: string[];
}) {
    const { t, tPath } = useTranslation();
    const pathname = usePathname();
    const languages = ALL_LANGUAGES.filter(l => supportedLocales.includes(l.code));
    const currencies = ALL_CURRENCIES.filter(c => supportedCurrencies.includes(c.code));
    const tree = buildCategoryTree(categories);
    const [catOpen, setCatOpen] = useState(false);
    // Index of the menu item whose sub-items are expanded (desktop shows them as a hover dropdown).
    const [openItem, setOpenItem] = useState<number | null>(null);
    const position = cfg.mobile_menu_position || 'left';
    const bgColor = cfg.mobile_menu_bg_color || cfg.bg_color || '#000000';
    const textColor = cfg.mobile_menu_text_color || cfg.text_color || '#ffffff';
    const fontFamily = cfg.mobile_menu_font_family || cfg.font_family || undefined;
    const fontSize = cfg.mobile_menu_font_size ? `${cfg.mobile_menu_font_size}px` : '15px';
    const fontWeight = cfg.mobile_menu_font_weight || '600';
    const alignment = cfg.mobile_menu_alignment || 'left';
    const itemSpacing = cfg.mobile_menu_item_spacing !== undefined ? `${cfg.mobile_menu_item_spacing}px` : '4px';
    const iconSize = cfg.icon_size_mobile ?? 20;

    let drawerClass = "fixed z-50 flex flex-col shadow-2xl overflow-y-auto ";
    if (position === 'top') {
        drawerClass += "top-0 left-0 right-0 max-h-[85vh] rounded-b-3xl border-b animate-in slide-in-from-top duration-300";
    } else if (position === 'right') {
        drawerClass += "top-0 right-0 bottom-0 w-80 max-w-[85vw] animate-in slide-in-from-right duration-300";
    } else if (position === 'fullscreen') {
        drawerClass += "inset-0 animate-in fade-in duration-300";
    } else {
        drawerClass += "top-0 left-0 bottom-0 w-80 max-w-[85vw] animate-in slide-in-from-left duration-300";
    }

    const alignClass = alignment === 'center' ? 'justify-center text-center' : alignment === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    if (!isOpen) return null;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/40 z-40 animate-in fade-in duration-300"
                onClick={onClose}
            />
            <div
                className={drawerClass}
                style={{ backgroundColor: bgColor, color: textColor, fontFamily }}
            >
                <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: `${textColor}22` }}>
                    <span className="font-bold text-lg" style={{ color: textColor, fontFamily }}>{t("nav.menu", "Menu")}</span>
                    <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity" style={{ color: textColor }} aria-label={t("nav.close_menu", "Zamknij menu")}>
                        <X style={{ width: iconSize, height: iconSize }} aria-hidden />
                    </button>
                </div>
                <nav className="flex-1 p-4" style={{ display: 'flex', flexDirection: 'column', gap: itemSpacing }}>
                    {menuItems.map((item, i) =>
                        item.is_category_tree ? (
                            <div key={i}>
                                <button
                                    onClick={() => setCatOpen(v => !v)}
                                    className={cn("w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:opacity-70 transition-opacity", alignClass)}
                                    style={{ color: textColor, fontSize, fontWeight, fontFamily }}
                                >
                                    <span>{item.label}</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform shrink-0 ${catOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {catOpen && tree.length > 0 && (
                                    <div className="ml-4 mt-1 space-y-1">
                                        {tree.map(node => (
                                            <Link key={node.id} href={tPath(`/kategoria/${node.slug}`)} onClick={onClose}
                                                className="block px-3 py-2 rounded-lg text-sm hover:opacity-70 transition-opacity"
                                                style={{ color: `${textColor}cc`, fontFamily }}>
                                                {node.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : item.children && item.children.length > 0 ? (
                            <div key={i}>
                                <div className="flex items-center gap-1">
                                    <Link href={item.url} onClick={onClose}
                                        className={cn("flex-1 flex items-center px-3 py-2.5 rounded-lg hover:opacity-70 transition-opacity", alignClass)}
                                        style={{
                                            color: textColor,
                                            fontSize,
                                            fontWeight,
                                            fontFamily,
                                            backgroundColor: pathname === item.url ? `${textColor}1a` : undefined,
                                        }}>
                                        {item.label}
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={() => setOpenItem(v => (v === i ? null : i))}
                                        aria-expanded={openItem === i}
                                        aria-label={t('nav.toggle_submenu', 'Pokaż podmenu: {label}').replace('{label}', item.label)}
                                        className="p-2.5 rounded-lg hover:opacity-70 transition-opacity shrink-0"
                                        style={{ color: textColor }}
                                    >
                                        <ChevronDown className={`w-4 h-4 transition-transform ${openItem === i ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>
                                {openItem === i && (
                                    <div className="ml-4 mt-1 space-y-1">
                                        {item.children.map((child, ci) => (
                                            <React.Fragment key={ci}>
                                                <Link href={child.url} onClick={onClose}
                                                    className={cn("block px-3 py-2 rounded-lg text-sm hover:opacity-70 transition-opacity", alignClass)}
                                                    style={{ color: `${textColor}cc`, fontFamily, backgroundColor: pathname === child.url ? `${textColor}1a` : undefined }}>
                                                    {child.label}
                                                </Link>
                                                {child.children?.map((grand, gi) => (
                                                    <Link key={gi} href={grand.url} onClick={onClose}
                                                        className={cn("block pl-7 pr-3 py-1.5 rounded-lg text-sm hover:opacity-70 transition-opacity", alignClass)}
                                                        style={{ color: `${textColor}aa`, fontFamily }}>
                                                        {grand.label}
                                                    </Link>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link key={i} href={item.url} onClick={onClose}
                                className={cn("flex items-center px-3 py-2.5 rounded-lg hover:opacity-70 transition-opacity", alignClass)}
                                style={{
                                    color: textColor,
                                    fontSize,
                                    fontWeight,
                                    fontFamily,
                                    backgroundColor: pathname === item.url ? `${textColor}1a` : undefined,
                                }}>
                                {item.label}
                            </Link>
                        )
                    )}
                </nav>
                <div className="p-4 border-t space-y-2" style={{ borderColor: `${textColor}22` }}>
                    {(cfg.icon_order?.length ? cfg.icon_order.filter((k): k is 'cart' | 'account' => k === 'cart' || k === 'account') : (['cart', 'account'] as const))
                        .filter((k) => !(k === 'cart' ? cfg.hide_cart_icon : cfg.hide_account_icon)).map((iconKey) =>
                        iconKey === 'cart' ? (
                            <Link key="cart" href={tPath("/koszyk")} onClick={onClose}
                                className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg hover:opacity-70 transition-opacity", alignClass)}
                                style={{ color: textColor, fontFamily }}>
                                <HeaderIconGlyph iconKey="cart" cfg={cfg} style={{ width: iconSize, height: iconSize }} />
                                <span className="text-sm font-medium">{t("nav.cart", "Koszyk")} {cartCount > 0 && `(${cartCount})`}</span>
                            </Link>
                        ) : (
                            <Link key="account" href={session ? tPath("/moje-konto") : tPath("/logowanie")} onClick={onClose}
                                className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg hover:opacity-70 transition-opacity", alignClass)}
                                style={{ color: textColor, fontFamily }}>
                                <HeaderIconGlyph iconKey="account" cfg={cfg} loggedIn={!!session} style={{ width: iconSize, height: iconSize }} />
                                <span className="text-sm font-medium">{session ? t("nav.account", "Moje konto") : t("nav.login", "Zaloguj się")}</span>
                            </Link>
                        )
                    )}
                </div>

                {/* JĘZYK & WALUTA W DRAWERZE */}
                {(cfg.show_language_selector || cfg.show_currency_selector) && (
                    <div className="p-5 border-t space-y-5" style={{ borderColor: `${textColor}22` }}>
                        {cfg.show_language_selector && (
                            <div className="space-y-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{t("nav.language", "Język")}</span>
                                {resolveSelectorLayout(cfg.mobile_menu_selector_style, languages.length) === 'dropdown' ? (
                                    <LanguageDropdown locale={locale} languages={languages} onChange={onLocaleChange} flagUrl={getFlagUrl} />
                                ) : (
                                    <div className="grid grid-cols-3 gap-2">
                                        {languages.map(lang => {
                                            const isSelected = lang.code === locale;
                                            return (
                                                <button
                                                    key={lang.code}
                                                    type="button"
                                                    onClick={() => onLocaleChange(lang.code)}
                                                    className={cn(
                                                        "flex flex-col items-center justify-center py-2.5 rounded-xl border text-xs font-semibold transition-all gap-1",
                                                        isSelected ? "bg-white text-slate-900 border-white shadow" : "hover:bg-white/5 border-transparent text-current opacity-85"
                                                    )}
                                                    style={isSelected ? { color: bgColor } : undefined}
                                                >
                                                    <img
                                                        src={getFlagUrl(lang.code)}
                                                        alt={lang.name}
                                                        className="object-cover rounded-sm shrink-0 mb-1"
                                                        style={{ width: '24px', height: '16px' }}
                                                    />
                                                    <span className="text-[10px] uppercase font-bold">{lang.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        {cfg.show_currency_selector && (
                            <div className="space-y-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">{t("nav.currency", "Waluta")}</span>
                                {resolveSelectorLayout(cfg.mobile_menu_selector_style, currencies.length) === 'dropdown' ? (
                                    <CurrencyDropdown currency={currency} currencies={currencies} onChange={onCurrencyChange} />
                                ) : (
                                    <div className="grid grid-cols-3 gap-2">
                                        {currencies.map(curr => {
                                            const isSelected = curr.code === currency;
                                            return (
                                                <button
                                                    key={curr.code}
                                                    type="button"
                                                    onClick={() => onCurrencyChange(curr.code)}
                                                    className={cn(
                                                        "flex flex-col items-center justify-center py-2 border text-xs font-bold transition-all gap-0.5 rounded-xl",
                                                        isSelected ? "bg-white text-slate-900 border-white shadow" : "hover:bg-white/5 border-transparent text-current opacity-85"
                                                    )}
                                                    style={isSelected ? { color: bgColor } : undefined}
                                                >
                                                    <span className="text-xs font-black">{curr.symbol}</span>
                                                    <span className="text-[9px] uppercase font-black">{curr.code}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

// ── Main Header ───────────────────────────────────────────────────────

// The category tree and modal items keep their own behaviour, so they stay in the bar
// instead of moving under "Więcej" (a plain submenu link there couldn't open a modal).
const isCollapsibleMenuItem = (item: MenuItemConfig) => !item.is_category_tree && !item.modal_enabled;

export function Header({ themeColor, siteName, headerConfig, logoUrl, logoSize, categories = [], brands = [] }: HeaderProps) {
    const { data: session } = useSession();
    const { locale, currency, setLocale, setCurrency, t, tPath, supportedLocales, supportedCurrencies } = useTranslation();
    const [cartCount, setCartCount] = useState(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    // Set by the current page's own first block (see HeaderTransparencyContext) —
    // NOT a global header setting, since which page has a hero (and what color
    // its text should be) varies per page.
    const headerTransparency = useHeaderTransparency();

    useEffect(() => {
        if (!headerTransparency.enabled) return;
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [headerTransparency.enabled]);

    useEffect(() => {
        const updateCart = () => {
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            const count = cart.reduce((acc: number, item: any) => acc + item.quantity, 0);
            setCartCount(count);
        };
        updateCart();
        window.addEventListener('cart-updated', updateCart);
        window.addEventListener('storage', updateCart);
        return () => {
            window.removeEventListener('cart-updated', updateCart);
            window.removeEventListener('storage', updateCart);
        };
    }, []);

    const handleCurrencyChange = (newCurrency: string) => {
        setCurrency(newCurrency);
    };

    const [subbarOpen, setSubbarOpen] = useState(false);
    const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [liveResults, setLiveResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
                setShowSearchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => {
        const term = searchQuery.trim();
        if (term.length < 2) {
            setLiveResults([]);
            setIsSearching(false);
            setShowSearchDropdown(false);
            return;
        }

        setIsSearching(true);
        setShowSearchDropdown(true);

        const timer = setTimeout(async () => {
            try {
                const data = await fetchAPI(`/products?search=${encodeURIComponent(term)}&limit=5`);
                const items = Array.isArray(data) ? data : (data?.items || []);
                setLiveResults(items);
            } catch (err) {
                console.error('Live search error:', err);
                setLiveResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 200);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // setLocale resolves the current page's translated slug (products, categories, pages) and the
    // target language's address itself.
    const handleLocaleChange = (newLocale: string) => setLocale(newLocale);

    // Products for leaf categories (no subcategories)
    const [categoryProducts, setCategoryProducts] = useState<Record<number, any[]>>({});
    const [categoryProductsLoading, setCategoryProductsLoading] = useState<number | null>(null);

    const tree = buildCategoryTree(categories);

    const activeDropdownNode = useMemo(() => {
        return tree.find(n => n.id === (hoveredCategory ?? tree[0]?.id)) || tree[0] || null;
    }, [hoveredCategory, tree]);

    useEffect(() => {
        if (!subbarOpen || !activeDropdownNode) return;
        if (activeDropdownNode.children.length > 0) return; // has subcategories — no need to fetch
        const id = activeDropdownNode.id;
        if (categoryProducts[id]) return; // already cached
        setCategoryProductsLoading(id);
        fetchAPI(`/products?category_id=${id}&limit=6&currency=${currency}`)
            .then((data: any) => {
                setCategoryProducts(prev => ({ ...prev, [id]: Array.isArray(data) ? data : (data?.items ?? []) }));
            })
            .catch(() => {
                setCategoryProducts(prev => ({ ...prev, [id]: [] }));
            })
            .finally(() => setCategoryProductsLoading(null));
    }, [subbarOpen, activeDropdownNode?.id, activeDropdownNode?.children.length, currency]);

    const isHomepage = pathname === '/' || pathname === '/pl' || pathname === '/en' || pathname === '/de';

    useEffect(() => {
        if (headerConfig?.subbar_expanded_homepage && isHomepage) {
            setSubbarOpen(true);
        } else {
            setSubbarOpen(false);
        }
    }, [pathname, isHomepage, headerConfig?.subbar_expanded_homepage]);

    const subbarCategoriesRef = useRef<HTMLDivElement>(null);
    
    const [activeModalItem, setActiveModalItem] = useState<MenuItemConfig | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (headerConfig?.subbar_expanded_homepage && isHomepage) {
                return;
            }
            if (subbarCategoriesRef.current && !subbarCategoriesRef.current.contains(event.target as Node)) {
                setSubbarOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [pathname, isHomepage, headerConfig?.subbar_expanded_homepage]);

    const getCategoryPath = (catId: number, flatCategories: CategoryFlat[]): string => {
        const path: string[] = [];
        let current = flatCategories.find(c => c.id === catId);
        while (current) {
            path.unshift(current.slug);
            const parentId = current.parent_id;
            if (parentId === null) break;
            current = flatCategories.find(c => c.id === parentId);
        }
        return path.join('/');
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            router.push(tPath(`/produkty?q=${encodeURIComponent(searchQuery.trim())}`));
        }
    };

    // Website without the shop module: no product search, cart, customer account or currency.
    const siteMode = isSiteMode(useSiteConfig());
    const cfg: HeaderConfig = siteMode
        ? { ...(headerConfig ?? {}), subbar_show_search: false, show_currency_selector: false, icon_order: ['language'] }
        : headerConfig ?? {};
    const height = cfg.height ?? 64;
    const heightMobile = cfg.height_mobile ?? height;
    const bgColor = cfg.bg_color ?? themeColor ?? 'var(--primary)';
    const textColor = cfg.text_color ?? '#ffffff';
    const borderStyle = cfg.border_style ?? 'none';
    const logoMaxHeight = cfg.logo_max_height ?? 40;
    const logoMaxHeightMobile = cfg.logo_max_height_mobile ?? logoMaxHeight;
    const logoMaxWidth = cfg.logo_max_width ?? 160;
    // The logo box has a fixed CSS height and `width: auto`, so until the file arrives the browser
    // takes its width from the width/height attributes' ratio. Those used to be max-width ×
    // max-height (e.g. 96×80 for a square 640×640 logo), so the box was drawn 20 % too wide and
    // snapped narrower when the image loaded, moving everything next to it. With the measured
    // intrinsic ratio the reserved box is exact; `sizes` then asks for the width it really renders.
    const logoRatio = logoSize && logoSize.width > 0 && logoSize.height > 0 ? logoSize.width / logoSize.height : null;
    const logoAttrWidth = logoRatio ? Math.max(1, Math.round(logoMaxHeight * logoRatio)) : (logoMaxWidth || 160);
    const logoSizes = logoRatio
        ? `(max-width: 1023px) ${Math.ceil(logoMaxHeightMobile * logoRatio)}px, ${Math.ceil(Math.min(logoMaxHeight * logoRatio, logoMaxWidth || Infinity))}px`
        : '(max-width: 640px) 120px, 160px';
    const logoPaddingX = cfg.logo_padding_x ?? 0;
    const logoPaddingY = cfg.logo_padding_y ?? 0;
    const iconDesktop = cfg.icon_size_desktop ?? 20;
    const iconMobile = cfg.icon_size_mobile ?? 20;

    // A website (no shop module) drops links into shop pages — they'd only redirect home.
    const linksIntoShop = (url?: string) => !!url && url.startsWith('/') && isShopStorefrontPath(url.split(/[?#]/)[0]);
    const rawMenuItems: MenuItemConfig[] = siteMode
        ? (cfg.menu_items ?? [{ label: 'Główna', url: '/', is_category_tree: false }])
            .filter(item => !item.is_category_tree && !linksIntoShop(item.url))
            .map(item => item.children ? { ...item, children: item.children.filter(c => !linksIntoShop(c.url)) } : item)
        : cfg.menu_items ?? [
            { label: 'Sklep', url: '/', is_category_tree: false },
            { label: 'Produkty', url: '/produkty', is_category_tree: false },
        ];

    // Collect labels that are NOT covered by the static map — these need auto-translation
    const labelsNeedingTranslation = rawMenuItems.flatMap(item => [
        item.label,
        ...(item.children?.map(c => c.label) || []),
    ]).filter(label => !item_has_key(label));

    function item_has_key(label: string) {
        return !!(LABEL_TO_KEY[label]);
    }

    const autoTranslated = useAutoTranslateLabels(labelsNeedingTranslation, locale);

    const menuItems = rawMenuItems.map(item => {
        const key = item.translation_key || LABEL_TO_KEY[item.label];
        return {
            ...item,
            label: key ? t(key, item.label) : (autoTranslated[item.label] || item.label),
            url: tPath(item.url),
            children: item.children?.map(child => {
                const childKey = child.translation_key || LABEL_TO_KEY[child.label];
                return {
                    ...child,
                    label: childKey ? t(childKey, child.label) : (autoTranslated[child.label] || child.label),
                    url: tPath(child.url),
                };
            }),
        };
    });

    const separatorColor = cfg.border_color || 'rgba(0,0,0,0.08)';
    const separatorWidth = cfg.border_width ?? 1;
    const separatorType = cfg.border_type || 'solid';
    const hasShadow = cfg.border_shadow ?? false;
    const shadowColor = cfg.border_shadow_color || 'rgba(0,0,0,0.06)';
    const shadowSize = cfg.border_shadow_size ?? 8;

    const headerBoxShadow = hasShadow
        ? `0 ${shadowSize}px ${shadowSize * 2}px 0 ${shadowColor}`
        : undefined;

    // Transparent-over-hero mode: header starts see-through, then fades to its
    // normal solid background/text colors once the page scrolls (or the mobile
    // drawer is open, so menu text stays legible against whatever's behind it).
    const isTransparentMode = headerTransparency.enabled;
    const isSolid = !isTransparentMode || scrolled || mobileOpen;
    const effectiveTextColor = isTransparentMode && !isSolid ? headerTransparency.textColor : textColor;
    const logoFilter = isTransparentMode && !isSolid && headerTransparency.logoWhite ? 'brightness(0) invert(1)' : undefined;
    // Passed to NavLink/icon renderers in place of `cfg` so every child that
    // only ever reads `cfg.text_color`/`cfg.bg_color` picks up the transparent
    // state for free, without threading extra props through each of them.
    const effectiveCfg: HeaderConfig = isTransparentMode
        ? { ...cfg, text_color: effectiveTextColor, bg_color: isSolid ? bgColor : 'transparent' }
        : cfg;

    const headerInlineStyle: React.CSSProperties = {
        backgroundColor: isTransparentMode ? (isSolid ? bgColor : 'transparent') : bgColor,
        color: effectiveTextColor,
        height: `${height}px`,
        transition: isTransparentMode ? 'background-color 300ms ease, color 300ms ease' : undefined,
        ...(borderStyle === 'bottom' && separatorWidth > 0 && isSolid ? { borderBottom: `${separatorWidth}px ${separatorType} ${separatorColor}` } : {}),
        ...(borderStyle === 'all' && separatorWidth > 0 && isSolid ? { border: `${separatorWidth}px ${separatorType} ${separatorColor}` } : {}),
        ...(headerBoxShadow && isSolid ? { boxShadow: headerBoxShadow } : {}),
    };

    const menuSignature = menuItems
        .map((item) => `${item.label}|${item.children?.length ?? 0}|${item.image_url ?? ''}|${item.is_bold ? 1 : 0}`)
        .join('§');

    const columnWidths = {
        logo: cfg.column_widths?.logo ?? 33,
        menu: cfg.column_widths?.menu ?? 34,
        icons: cfg.column_widths?.icons ?? 33,
    };

    // Unified, reorderable set of header icons/selectors — same order drives
    // both the desktop row and the compact mobile row.
    const iconOrder: ('cart' | 'account' | 'language' | 'currency')[] =
        cfg.icon_order?.length === 4 ? cfg.icon_order : ['language', 'currency', 'cart', 'account'];

    function renderIconItem(key: 'cart' | 'account' | 'language' | 'currency', isMobile: boolean) {
        switch (key) {
            case 'language':
                return cfg.show_language_selector ? (
                    <LanguageSelector key="language" cfg={effectiveCfg} currentLocale={locale} onChange={handleLocaleChange} isMobile={isMobile} supportedLocales={supportedLocales} />
                ) : null;
            case 'currency':
                return cfg.show_currency_selector ? (
                    <CurrencySelector key="currency" cfg={effectiveCfg} currentCurrency={currency} onChange={handleCurrencyChange} isMobile={isMobile} supportedCurrencies={supportedCurrencies} />
                ) : null;
            case 'cart':
                return siteMode || cfg.hide_cart_icon ? null : (
                    <Link key="cart" href={tPath("/koszyk")} className="relative p-1.5 hover:opacity-70 transition-opacity rounded" style={{ transition: isTransparentMode ? 'color 300ms ease' : undefined }}>
                        <HeaderIconGlyph iconKey="cart" cfg={effectiveCfg} style={{ width: 'var(--header-icon-size)', height: 'var(--header-icon-size)', color: effectiveTextColor }} />
                        <span className="sr-only">{t("nav.cart", "Koszyk")}</span>
                        {cartCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold"
                                style={{ backgroundColor: effectiveTextColor, color: isSolid ? bgColor : '#000000' }}>
                                {cartCount}
                            </span>
                        )}
                    </Link>
                );
            case 'account':
                return siteMode || cfg.hide_account_icon ? null : (
                    <Link key="account" href={session ? tPath("/moje-konto") : tPath("/logowanie")} className="p-1.5 hover:opacity-70 transition-opacity rounded">
                        <HeaderIconGlyph iconKey="account" cfg={effectiveCfg} loggedIn={!!session} style={{ width: 'var(--header-icon-size)', height: 'var(--header-icon-size)', color: effectiveTextColor }} />
                        <span className="sr-only">{session ? t("nav.account", "Moje konto") : t("nav.login", "Zaloguj się")}</span>
                    </Link>
                );
        }
    }

    // Which zone (left/center/right) each of the mobile top bar's three structural pieces
    // lands in — e.g. lets a merchant put the logo dead-center with the hamburger on the left
    // and icons on the right, instead of the fixed "logo left, everything else right" layout.
    const mobileHeaderLayout = {
        logo: cfg.mobile_header_layout?.logo ?? 'left',
        icons: cfg.mobile_header_layout?.icons ?? 'right',
        hamburger: cfg.mobile_header_layout?.hamburger ?? 'right',
    } as const;

    // The mobile top bar and the desktop grid below are both always mounted (Tailwind's
    // `lg:hidden` / `hidden lg:grid` only toggles `display`, it doesn't unmount either one), so
    // rendering the logo into both means two <Image priority> instances competing for the
    // highest fetch priority on every page load. Only the mobile-zone copy (mounted first, and
    // the one actually visible on the far more common mobile viewport) carries `priority` —
    // the desktop copy still loads eagerly by default, just without the duplicate high-priority
    // preload hint. Neither copy asks for fetchpriority=high: the logo is never the LCP, and with
    // it Chrome fetched the logo ahead of the hero/product photo (which was the LCP) on every page.
    const renderLogo = (priority: boolean) => (
        <Link href="/" className="flex items-center shrink min-w-0" style={{ padding: `${logoPaddingY}px ${logoPaddingX}px` }}>
            <style>{`
                .site-logo {
                    max-height: ${logoMaxHeight}px;
                    height: ${logoMaxHeight}px;
                }
                .site-logo svg {
                    max-height: 100%;
                    height: 100% !important;
                    width: auto !important;
                }
                @media (max-width: 1023px) {
                    .site-logo {
                        max-height: ${logoMaxHeightMobile}px !important;
                        height: ${logoMaxHeightMobile}px !important;
                        max-width: 100% !important;
                    }
                }
            `}</style>
            {logoUrl ? (
                (logoUrl.toLowerCase().endsWith('.json') || logoUrl.toLowerCase().includes('.lottie')) ? (
                    <div
                        className="site-logo flex items-center justify-center shrink-0"
                        style={{
                            maxWidth: logoMaxWidth ? `${logoMaxWidth}px` : undefined,
                            width: 'auto',
                            filter: logoFilter,
                        }}
                    >
                        <LottiePlayer
                            src={logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`}
                            ariaLabel={siteName}
                            style={{ height: '100%', width: 'auto' }}
                        />
                    </div>
                ) : (
                    <Image
                        src={toOptimizableSrc(logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`)}
                        alt={siteName}
                        width={logoAttrWidth}
                        height={logoMaxHeight}
                        sizes={logoSizes}
                        className="object-contain site-logo"
                        style={{ maxWidth: logoMaxWidth ? `${logoMaxWidth}px` : undefined, width: 'auto', filter: logoFilter }}
                        priority={priority}
                        fetchPriority="auto"
                    />
                )
            ) : (
                <Image
                    src="/branding/logo.png"
                    alt={siteName || 'ShopStag'}
                    width={logoMaxWidth || 160}
                    height={logoMaxHeight}
                    sizes="(max-width: 640px) 120px, 160px"
                    className="object-contain site-logo"
                    style={{ maxWidth: logoMaxWidth ? `${logoMaxWidth}px` : undefined, width: 'auto' }}
                    priority={priority}
                    fetchPriority="auto"
                />
            )}
        </Link>
    );
    const logoContent = renderLogo(true);
    const desktopLogoContent = renderLogo(false);

    const mobileHamburgerButton = (
        <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 hover:opacity-70 transition-opacity rounded flex items-center justify-center"
            style={{ color: textColor }}
            aria-label="Otwórz menu"
        >
            <HeaderIconGlyph iconKey="hamburger" cfg={effectiveCfg} style={{ width: 'var(--header-icon-size)', height: 'var(--header-icon-size)', color: textColor }} />
        </button>
    );

    // Language and currency already have full, accessible selectors in the mobile
    // drawer. Repeating their labelled desktop controls in the narrow top bar can
    // squeeze a centred logo between the hamburger, cart and account actions.
    const mobileIconOrder = iconOrder.filter((key) => key === 'cart' || key === 'account');
    const mobileIconsCluster = (
        <div className="flex items-center gap-1">
            {mobileIconOrder.map((key) => renderIconItem(key, true))}
        </div>
    );

    const mobileZones: Record<'left' | 'center' | 'right', React.ReactNode[]> = { left: [], center: [], right: [] };
    mobileZones[mobileHeaderLayout.logo].push(<React.Fragment key="logo">{logoContent}</React.Fragment>);
    mobileZones[mobileHeaderLayout.icons].push(<React.Fragment key="icons">{mobileIconsCluster}</React.Fragment>);
    mobileZones[mobileHeaderLayout.hamburger].push(<React.Fragment key="hamburger">{mobileHamburgerButton}</React.Fragment>);

    return (
        <>
            <style>{`
                :root {
                    --header-icon-size: ${iconDesktop}px;
                    --header-flag-height: ${Math.max(10, Math.round(iconDesktop * 0.7))}px;
                    --header-flag-width: ${Math.max(15, Math.round(iconDesktop * 0.7 * 1.5))}px;
                    --header-currency-font-size: ${Math.max(9, Math.round(iconDesktop * 0.55))}px;
                }
                @media (max-width: 767px) {
                    :root {
                        --header-icon-size: ${iconMobile}px;
                        --header-flag-height: ${Math.max(10, Math.round(iconMobile * 0.7))}px;
                        --header-flag-width: ${Math.max(15, Math.round(iconMobile * 0.7 * 1.5))}px;
                        --header-currency-font-size: ${Math.max(9, Math.round(iconMobile * 0.55))}px;
                    }
                    .site-header {
                        height: ${heightMobile}px !important;
                    }
                }
            `}</style>
            <div className={cn(
                "w-full flex flex-col",
                isSolid && "shadow-sm",
                isTransparentMode ? "fixed inset-x-0 top-0 z-30" : headerConfig?.sticky !== false ? "sticky top-0 z-30" : "relative"
            )} style={isTransparentMode ? { top: 'var(--alert-bar-h, 0px)' } : undefined}>
                {headerConfig?.topbar_enabled && (
                    <div
                        className="w-full px-4 flex items-center transition-all z-40 overflow-hidden"
                        style={{
                            height: `${headerConfig.topbar_height || 36}px`,
                            backgroundColor: headerConfig.topbar_bg_color || '#0f172a',
                            color: headerConfig.topbar_text_color || '#ffffff',
                            fontSize: `${headerConfig.topbar_font_size || 11}px`,
                            fontWeight: headerConfig.topbar_font_weight || 600,
                            justifyContent: headerConfig.topbar_alignment === 'center' ? 'center' : headerConfig.topbar_alignment === 'between' ? 'space-between' : headerConfig.topbar_alignment === 'right' ? 'flex-end' : 'flex-start',
                        }}
                    >
                        <div className="flex items-center gap-3 truncate">
                            {splitPhones(headerConfig.topbar_phone).map((phone, i) => (
                                <a key={phone} href={telHref(phone)} onClick={() => trackClick('tel', phone)} className="hidden sm:inline-flex items-center gap-1 opacity-90 hover:opacity-100 shrink-0">
                                    {i === 0 ? '📞 ' : ''}{phone}
                                </a>
                            ))}
                            {headerConfig.topbar_phone && headerConfig.topbar_email && (
                                <span className="hidden sm:inline opacity-40 shrink-0">/</span>
                            )}
                            {headerConfig.topbar_email && (
                                <a href={`mailto:${headerConfig.topbar_email}`} onClick={() => trackClick('mailto', headerConfig.topbar_email || '')} className="hidden sm:inline-flex items-center gap-1 opacity-90 hover:opacity-100 shrink-0">
                                    ✉️ {headerConfig.topbar_email}
                                </a>
                            )}
                            {headerConfig.topbar_content ? (
                                headerConfig.topbar_link ? (
                                    <Link href={headerConfig.topbar_link} className="hover:underline truncate font-semibold">
                                        {headerConfig.topbar_content}
                                    </Link>
                                ) : (
                                    <span className="truncate">{headerConfig.topbar_content}</span>
                                )
                            ) : (
                                <span className="truncate">🎉 Darmowa dostawa dla zamówień od 200 PLN</span>
                            )}
                            {headerConfig.topbar_badge_text && (
                                <span
                                    className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider shrink-0"
                                    style={{
                                        backgroundColor: headerConfig.topbar_badge_bg || '#2563eb',
                                        color: headerConfig.topbar_badge_text_color || '#ffffff',
                                    }}
                                >
                                    {headerConfig.topbar_badge_text}
                                </span>
                            )}
                        </div>
                        {(headerConfig.topbar_show_socials && headerConfig.topbar_social_links?.length) || (headerConfig.topbar_featured_brand_ids?.length && brands.length) ? (
                            <div className="hidden md:flex items-center gap-3 ml-auto pl-4 shrink-0">
                                {headerConfig.topbar_show_socials && headerConfig.topbar_social_links && headerConfig.topbar_social_links.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        {headerConfig.topbar_social_links.map((s, i) => (
                                            <a key={i} href={s.url || '#'} target="_blank" rel="noopener noreferrer" title={s.platform} className="opacity-80 hover:opacity-100 transition-opacity">
                                                <TopbarSocialIcon platform={s.platform} />
                                            </a>
                                        ))}
                                    </div>
                                )}
                                {headerConfig.topbar_featured_brand_ids && headerConfig.topbar_featured_brand_ids.length > 0 && brands.length > 0 && (
                                    <div className="hidden lg:flex items-center gap-3">
                                        {brands
                                            .filter(b => headerConfig.topbar_featured_brand_ids!.includes(b.id))
                                            .map(b => b.logo_url ? (
                                                // Lazy: this row only shows from lg up, and a lazy image that isn't
                                                // rendered is never fetched — phones used to download every logo
                                                // (dlaszwalni: Jack 1024×579, 33 KB) for nothing. Resized with room to spare for a
                                                // 20 px row (a 16:1 logo at DPR 2), at the top quality — sharp logo edges.
                                                // eslint-disable-next-line @next/next/no-img-element
                                                <img
                                                    key={b.id}
                                                    src={optimizedImageUrl(getAbsoluteMediaUrl(b.logo_url), 640, 95)}
                                                    alt={b.name}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="h-5 w-auto object-contain opacity-90"
                                                />
                                            ) : null)}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                )}
                <header
                    className="w-full site-header relative z-30"
                    style={headerInlineStyle}
                >
                    {/* Mobile top bar: three independently positionable zones (left/center/right) —
                        e.g. logo centered with the hamburger on the left and icons on the right —
                        driven by cfg.mobile_header_layout. Desktop keeps its own grid below,
                        hidden here so nothing renders twice. */}
                    <div className="h-full flex items-center lg:hidden px-3">
                        <div className="flex-1 flex items-center justify-start gap-2 min-w-0">{mobileZones.left}</div>
                        <div className="flex items-center justify-center gap-2 shrink-0">{mobileZones.center}</div>
                        <div className="flex-1 flex items-center justify-end gap-2 min-w-0">{mobileZones.right}</div>
                    </div>

                    {cfg.nav_layout === 'two_rows' ? (
                        <div className="hidden lg:flex flex-col w-full px-3 md:px-[10%]">
                            <div className="flex items-center justify-between py-2.5 w-full">
                                <div className="flex items-center shrink min-w-0">
                                    {desktopLogoContent}
                                </div>
                                <div className="flex items-center justify-end gap-1.5 shrink-0">
                                    {iconOrder.map((key) => renderIconItem(key, false))}
                                </div>
                            </div>
                            <nav
                                className="flex flex-wrap justify-center items-center gap-2 py-2 border-t border-slate-100/40 w-full"
                            >
                                {menuItems.map((item, i) =>
                                    item.is_category_tree ? (
                                        !cfg.subbar_enabled && <CategoryMenuDropdown key={i} label={item.label} categories={categories} cfg={effectiveCfg} />
                                    ) : (
                                        <NavLink key={i} item={item} cfg={effectiveCfg} isSolid={isSolid} onOpenModal={setActiveModalItem} />
                                    )
                                )}
                            </nav>
                        </div>
                    ) : (
                        <div
                            className="h-full hidden lg:grid px-3 md:px-[10%]"
                            // The configured percentages hold whenever everything fits. The logo and icons
                            // columns never get narrower than their content (the logo and the language/
                            // currency selectors used to spill over the menu); the menu takes what's left.
                            style={{ gridTemplateColumns: `minmax(max-content, ${columnWidths.logo}%) minmax(0, 1fr) minmax(max-content, ${columnWidths.icons}%)` }}
                        >

                            {/* Col 1 — Logo */}
                            <div className="flex items-center shrink min-w-0 max-w-[28vw] lg:max-w-none">
                                {desktopLogoContent}
                            </div>

                            {/* Col 2 — Nav (desktop only): one row, the rest under "Więcej". */}
                            <DesktopNav
                                className="hidden lg:flex justify-center items-center gap-1 self-stretch h-full"
                                items={menuItems}
                                signature={menuSignature}
                                canCollapse={isCollapsibleMenuItem}
                                renderItem={(item, i) =>
                                    item.is_category_tree ? (
                                        !cfg.subbar_enabled && <CategoryMenuDropdown key={i} label={item.label} categories={categories} cfg={effectiveCfg} />
                                    ) : (
                                        <NavLink key={i} item={item} cfg={effectiveCfg} isSolid={isSolid} onOpenModal={setActiveModalItem} />
                                    )
                                }
                                renderMore={(overflow) => (
                                    <NavLink
                                        item={{ label: t('nav.more', 'Więcej'), url: '#', is_category_tree: false, children: overflow }}
                                        cfg={effectiveCfg}
                                        isSolid={isSolid}
                                        alignDropdown="right"
                                    />
                                )}
                            />

                            {/* Col 3 — Icons / Selectors (order + visibility driven by cfg.icon_order).
                                Desktop-only now — the mobile icon row + hamburger render via
                                mobileZones above instead, positioned per cfg.mobile_header_layout. */}
                            <div className="flex items-center justify-end gap-1.5 shrink-0">
                                {iconOrder.map((key) => renderIconItem(key, false))}
                            </div>
                        </div>
                    )}
                </header>

                {/* ADDITIONAL CATEGORIES & SEARCH SUB-BAR */}
                {cfg.subbar_enabled && (
                    <>
                        <style>{`
                            .site-header-subbar { height: ${cfg.subbar_height || 48}px; }
                            @media (max-width: 767px) { 
                                .site-header-subbar { height: ${cfg.subbar_height_mobile || 48}px !important; } 
                            }

                            /* Kategorie Przycisk */
                            .site-subbar-cat-btn {
                                background-color: ${cfg.subbar_button_bg || '#000000'} !important;
                                color: ${cfg.subbar_button_text_color || '#ffffff'} !important;
                                border-radius: ${cfg.subbar_button_border_radius !== undefined ? cfg.subbar_button_border_radius : 8}px !important;
                            }
                            .site-subbar-cat-btn:hover {
                                background-color: ${cfg.subbar_button_hover_bg || '#1e293b'} !important;
                                color: ${cfg.subbar_button_hover_text || '#ffffff'} !important;
                            }

                            /* Mega Menu Dropdown */
                            .site-subbar-dropdown {
                                background-color: ${cfg.subbar_dropdown_bg || '#ffffff'} !important;
                                border-radius: ${cfg.dropdown_border_radius ?? 16}px !important;
                                color: ${cfg.subbar_dropdown_text_color || '#1f2937'} !important;
                            }
                            .site-subbar-dropdown-sidebar {
                                background-color: ${cfg.subbar_dropdown_sidebar_bg || '#f8fafc'} !important;
                            }

                            /* Lewa Lista Kategorii */
                            .site-subbar-dropdown-cat-item {
                                color: ${cfg.subbar_dropdown_text_color || '#1f2937'} !important;
                                border-left-color: transparent !important;
                            }
                            .site-subbar-dropdown-cat-item.active {
                                background-color: ${cfg.subbar_dropdown_active_bg || '#eff6ff'} !important;
                                color: ${cfg.subbar_dropdown_active_text_color || '#2563eb'} !important;
                                border-left-color: ${cfg.subbar_dropdown_active_text_color || '#2563eb'} !important;
                            }
                            .site-subbar-dropdown-cat-item:hover {
                                background-color: ${cfg.subbar_dropdown_hover_bg || '#f1f5f9'} !important;
                                color: ${cfg.subbar_dropdown_hover_text || '#1e293b'} !important;
                            }

                            /* Prawa Lista Podkategorii */
                            .site-subbar-dropdown-sub1-link {
                                color: ${cfg.subbar_dropdown_text_color || '#1f2937'} !important;
                            }
                            .site-subbar-dropdown-sub1-link:hover {
                                color: ${cfg.subbar_dropdown_active_text_color || '#2563eb'} !important;
                            }
                            .site-subbar-dropdown-sub2-link {
                                color: ${cfg.subbar_dropdown_text_color || '#1f2937'} !important;
                                opacity: 0.7 !important;
                            }
                            .site-subbar-dropdown-sub2-link:hover {
                                color: ${cfg.subbar_dropdown_active_text_color || '#2563eb'} !important;
                                opacity: 1 !important;
                            }

                            /* Wyszukiwarka */
                            .site-subbar-search-input {
                                background-color: ${cfg.subbar_search_input_bg || '#ffffff'} !important;
                                border-color: ${cfg.subbar_search_input_border_color || '#e2e8f0'} !important;
                                color: ${cfg.subbar_search_input_text_color || '#1f2937'} !important;
                                border-radius: ${cfg.subbar_search_input_border_radius !== undefined ? cfg.subbar_search_input_border_radius : 8}px !important;
                            }
                            .site-subbar-search-input::placeholder {
                                color: ${cfg.subbar_search_input_placeholder_color || '#94a3b8'} !important;
                            }
                            .site-subbar-search-button {
                                background-color: ${cfg.subbar_search_button_bg || '#2563eb'} !important;
                                color: ${cfg.subbar_search_button_text_color || '#ffffff'} !important;
                                border-radius: ${cfg.subbar_search_button_border_radius !== undefined ? cfg.subbar_search_button_border_radius : 8}px !important;
                            }
                            .site-subbar-search-button:hover {
                                background-color: ${cfg.subbar_search_button_hover_bg || '#1d4ed8'} !important;
                                color: ${cfg.subbar_search_button_hover_text || '#ffffff'} !important;
                                opacity: 1 !important;
                            }
                        `}</style>
                        <div
                            className="w-full relative site-header-subbar z-20"
                            style={{
                                backgroundColor: cfg.subbar_bg_color || '#f8fafc',
                                color: cfg.subbar_text_color || '#1f2937',
                                ...(cfg.subbar_border_style === 'bottom' && (cfg.subbar_border_width ?? 1) > 0 ? { borderBottom: `${cfg.subbar_border_width ?? 1}px ${cfg.subbar_border_type || 'solid'} ${cfg.subbar_border_color || 'rgba(0,0,0,0.06)'}` } : {}),
                                ...(cfg.subbar_border_style === 'all' && (cfg.subbar_border_width ?? 1) > 0 ? { border: `${cfg.subbar_border_width ?? 1}px ${cfg.subbar_border_type || 'solid'} ${cfg.subbar_border_color || 'rgba(0,0,0,0.06)'}` } : {}),
                                ...(cfg.subbar_border_shadow && cfg.subbar_border_shadow_size ? { boxShadow: `0 ${cfg.subbar_border_shadow_size}px ${cfg.subbar_border_shadow_size * 2}px 0 ${cfg.subbar_border_shadow_color || 'rgba(0,0,0,0.04)'}` } : {}),
                            }}
                        >
                            <div className="h-full flex items-center justify-between px-3 md:px-[10%] gap-4 w-full min-w-0">
                                {/* Desktop: Wszystkie kategorie button */}
                                <div className="hidden md:block relative shrink-0" ref={subbarCategoriesRef}>
                                    <button
                                        type="button"
                                        onClick={() => setSubbarOpen(!subbarOpen)}
                                        className="flex items-center gap-2 px-4 py-2 text-xs font-extrabold uppercase tracking-wider transition-all site-subbar-cat-btn"
                                    >
                                        <Grid className="w-4 h-4" />
                                        <span>{t("nav.all_categories", "Wszystkie kategorie")}</span>
                                        <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", subbarOpen && "rotate-180")} />
                                    </button>

                                    {/* MEGA MENU DROPDOWN PANEL */}
                                    {subbarOpen && tree.length > 0 && (
                                        <div
                                            className="absolute left-0 top-full mt-2 w-[80vw] max-w-5xl z-[100] flex border border-slate-155 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300 backdrop-blur-md site-subbar-dropdown"
                                            style={{
                                                boxShadow: cfg.dropdown_shadow !== false ? '0 20px 50px -12px rgba(0,0,0,0.15)' : undefined,
                                                minHeight: '400px',
                                                maxHeight: '520px',
                                            }}
                                        >
                                            {/* Left Column: Top Level Categories list */}
                                            <div className="w-64 border-r border-slate-100/50 py-3 overflow-y-auto shrink-0 site-subbar-dropdown-sidebar">
                                                {tree.map(node => {
                                                    const isHovered = hoveredCategory === node.id || (hoveredCategory === null && tree[0].id === node.id);
                                                    return (
                                                        <button
                                                            key={node.id}
                                                            type="button"
                                                            className={cn(
                                                                "w-full text-left px-5 py-3.5 text-sm font-bold flex items-center justify-between transition-all duration-200 border-l-4 site-subbar-dropdown-cat-item",
                                                                isHovered && "active"
                                                            )}
                                                            onMouseEnter={() => setHoveredCategory(node.id)}
                                                            onClick={() => {
                                                                setSubbarOpen(false);
                                                                router.push(tPath(`/kategoria/${getCategoryPath(node.id, categories)}`));
                                                            }}
                                                        >
                                                            <span>{node.name}</span>
                                                            {node.children.length > 0 && <ChevronRight className="w-4 h-4 opacity-50" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Right Column: Subcategories or Product Tiles */}
                                            <div className="flex-1 p-8 overflow-y-auto bg-white/40">
                                                {(() => {
                                                    const activeNode = tree.find(n => n.id === (hoveredCategory ?? tree[0].id)) || tree[0];
                                                    if (!activeNode) return null;

                                                    if (activeNode.children.length > 0) {
                                                        return (
                                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
                                                                {activeNode.children.map(sub1 => (
                                                                    <div key={sub1.id} className="space-y-3">
                                                                        <Link
                                                                            href={tPath(`/kategoria/${getCategoryPath(sub1.id, categories)}`)}
                                                                            className="font-extrabold text-sm transition-colors block leading-tight site-subbar-dropdown-sub1-link"
                                                                            onClick={() => setSubbarOpen(false)}
                                                                        >
                                                                            {sub1.name}
                                                                        </Link>
                                                                        {sub1.children.length > 0 && (
                                                                            <ul className="space-y-2 pl-0.5">
                                                                                {sub1.children.map(sub2 => (
                                                                                    <li key={sub2.id}>
                                                                                        <Link
                                                                                            href={tPath(`/kategoria/${getCategoryPath(sub2.id, categories)}`)}
                                                                                            className="text-xs font-medium transition-colors block site-subbar-dropdown-sub2-link"
                                                                                            onClick={() => setSubbarOpen(false)}
                                                                                        >
                                                                                            {sub2.name}
                                                                                        </Link>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }

                                                    // Leaf category — show product tiles
                                                    const prods = categoryProducts[activeNode.id];
                                                    const isLoading = categoryProductsLoading === activeNode.id;
                                                    const categoryHref = tPath(`/kategoria/${getCategoryPath(activeNode.id, categories)}`);

                                                    if (isLoading || (!prods && !isLoading)) {
                                                        return (
                                                            <div className="grid grid-cols-3 gap-4">
                                                                {Array.from({ length: 6 }).map((_, i) => (
                                                                    <div key={i} className="rounded-xl bg-slate-100 animate-pulse overflow-hidden">
                                                                        <div className="aspect-square bg-slate-200" />
                                                                        <div className="p-3 space-y-2">
                                                                            <div className="h-3 bg-slate-200 rounded w-3/4" />
                                                                            <div className="h-3 bg-slate-200 rounded w-1/2" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }

                                                    if (!prods || prods.length === 0) {
                                                        return (
                                                            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                                                <div className="text-3xl mb-2">📁</div>
                                                                <p className="text-xs font-semibold">Ta kategoria nie posiada jeszcze produktów.</p>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="space-y-4">
                                                            <div className="grid grid-cols-3 gap-4">
                                                                {prods.map((p: any) => {
                                                                    const mainImage = p.images?.find((img: any) => img.is_main) || p.images?.[0];
                                                                    const imgSrc = mainImage ? toOptimizableSrc(getAbsoluteMediaUrl(mainImage.thumbnail_path || mainImage.path)) : null;
                                                                    const productHref = tPath(`/produkty/${p.slug}`);
                                                                    const displayPrice = p.price_on_request ? priceOnRequestLabel(p, t) : (p.sales_price ?? p.price)?.toFixed(2);
                                                                    return (
                                                                        <Link
                                                                            key={p.id}
                                                                            href={productHref}
                                                                            onClick={() => setSubbarOpen(false)}
                                                                            className="group rounded-xl border border-slate-100 bg-white overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 block"
                                                                        >
                                                                            <div className="aspect-square bg-slate-50 relative overflow-hidden">
                                                                                {imgSrc ? (
                                                                                    <Image
                                                                                        src={imgSrc}
                                                                                        alt={mainImage?.alt_text || p.name}
                                                                                        fill
                                                                                        sizes="140px"
                                                                                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                                                                                    />
                                                                                ) : (
                                                                                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-2xl">📦</div>
                                                                                )}
                                                                            </div>
                                                                            <div className="p-2.5">
                                                                                <p className="text-xs font-bold text-slate-800 line-clamp-2 leading-tight mb-1">{p.name}</p>
                                                                                {displayPrice && (
                                                                                    <p className="text-xs font-black" style={{ color: cfg.subbar_dropdown_active_text_color || '#2563eb' }}>
                                                                                        {displayPrice}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="text-center pt-1">
                                                                <Link
                                                                    href={categoryHref}
                                                                    onClick={() => setSubbarOpen(false)}
                                                                    className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg transition-all hover:opacity-80"
                                                                    style={{ color: cfg.subbar_dropdown_active_text_color || '#2563eb', backgroundColor: cfg.subbar_dropdown_active_bg || '#eff6ff' }}
                                                                >
                                                                    Zobacz wszystkie produkty
                                                                    <ChevronRight className="w-3.5 h-3.5" />
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Wyszukiwarka (full-width on mobile, max-width on desktop) */}
                                {cfg.subbar_show_search && (
                                    <div ref={searchContainerRef} className="flex-1 max-w-xl md:max-w-3xl relative w-full min-w-0">
                                        <form
                                            onSubmit={(e) => {
                                                setShowSearchDropdown(false);
                                                handleSearchSubmit(e);
                                            }}
                                            toolname="search_products"
                                            tooldescription="Przeszukiwanie katalogu produktów wg nazwy lub słów kluczowych"
                                            data-webmcp-form-name="search_products"
                                            data-webmcp-tool-name="search_products"
                                            data-webmcp-description="Przeszukiwanie katalogu produktów wg nazwy lub słów kluczowych"
                                            aria-label="Wyszukiwarka produktów"
                                            className="w-full flex items-center relative min-w-0"
                                        >
                                            <div className="relative w-full flex items-center min-w-0">
                                                <Search 
                                                    className="absolute left-4 w-4 h-4 pointer-events-none z-10" 
                                                    style={{ color: cfg.subbar_search_input_placeholder_color || '#94a3b8' }}
                                                />
                                                <input
                                                    type="text"
                                                    param-name="query"
                                                    param-description="Szukana fraza produktu"
                                                    placeholder={t("nav.search", "Szukaj produktów...")}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onFocus={() => {
                                                        if (searchQuery.trim().length >= 2) setShowSearchDropdown(true);
                                                    }}
                                                    className="w-full min-w-0 pl-11 pr-24 py-2 h-9 text-xs focus:outline-none transition-all shadow-sm font-semibold site-subbar-search-input"
                                                />
                                                <button
                                                    type="submit"
                                                    className="absolute right-1 top-1 bottom-1 px-4 text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center site-subbar-search-button z-10"
                                                >
                                                    {t("nav.search_btn", "Szukaj...")}
                                                </button>
                                            </div>
                                        </form>

                                        {/* Live Autocomplete Dropdown */}
                                        {showSearchDropdown && searchQuery.trim().length >= 2 && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-2xl border border-slate-200/90 shadow-2xl overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2 duration-200 text-left">
                                                {isSearching ? (
                                                    <div className="p-4 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
                                                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                                        Szukanie produktów na żywo...
                                                    </div>
                                                ) : liveResults.length > 0 ? (
                                                    <div className="divide-y divide-slate-100">
                                                        <div className="p-2 space-y-1">
                                                            {liveResults.map((p: any) => {
                                                                const mainImage = p.images?.find((img: any) => img.is_main) || p.images?.[0];
                                                                const imgSrc = mainImage ? toOptimizableSrc(getAbsoluteMediaUrl(mainImage.thumbnail_path || mainImage.path)) : null;
                                                                const productHref = tPath(`/produkty/${p.slug}`);
                                                                const amount = (p.sales_price ?? p.price)?.toFixed(2);
                                                                const displayPrice = p.price_on_request ? priceOnRequestLabel(p, t) : (amount ? `${amount} ${currency}` : null);
                                                                return (
                                                                    <Link
                                                                        key={p.id}
                                                                        href={productHref}
                                                                        onClick={() => {
                                                                            setShowSearchDropdown(false);
                                                                            setSearchQuery('');
                                                                        }}
                                                                        className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors group"
                                                                    >
                                                                        <div className="w-10 h-10 rounded-lg bg-slate-100 relative overflow-hidden shrink-0 border border-slate-200 flex items-center justify-center">
                                                                            {imgSrc ? (
                                                                                <Image src={imgSrc} alt={p.name} fill sizes="40px" className="object-cover group-hover:scale-105 transition-transform" />
                                                                            ) : (
                                                                                <span className="text-xs">📦</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-xs font-bold text-slate-900 truncate group-hover:text-primary transition-colors">{p.name}</p>
                                                                            {p.category_name && (
                                                                                <p className="text-[10px] text-slate-400 font-medium truncate">{p.category_name}</p>
                                                                            )}
                                                                        </div>
                                                                        {displayPrice && (
                                                                            <div className="text-right shrink-0">
                                                                                <span className="text-xs font-extrabold text-slate-900">{displayPrice}</span>
                                                                            </div>
                                                                        )}
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                setShowSearchDropdown(false);
                                                                handleSearchSubmit(e);
                                                            }}
                                                            className="w-full p-2.5 bg-slate-50 hover:bg-slate-100 text-xs font-bold text-primary flex items-center justify-center gap-1 transition-colors border-t border-slate-100"
                                                        >
                                                            Zobacz wszystkie wyniki dla &quot;{searchQuery.trim()}&quot; <ChevronRight className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="p-4 text-center text-xs text-slate-500 font-medium">
                                                        Brak produktów pasujących do &quot;{searchQuery.trim()}&quot;
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <MobileMenu
                isOpen={mobileOpen}
                onClose={() => setMobileOpen(false)}
                menuItems={menuItems}
                categories={categories}
                session={session}
                cartCount={cartCount}
                cfg={cfg}
                locale={locale}
                onLocaleChange={handleLocaleChange}
                currency={currency}
                onCurrencyChange={handleCurrencyChange}
                supportedLocales={supportedLocales}
                supportedCurrencies={supportedCurrencies}
            />

            {activeModalItem && (
                <HeaderLinkModal item={activeModalItem} onClose={() => setActiveModalItem(null)} />
            )}
        </>
    );
}
